import concat from "lodash-es/concat"
import eq from "lodash-es/eq"
import filter from "lodash-es/filter"
import forEach from "lodash-es/forEach"
import isEmpty from "lodash-es/isEmpty"
import isNull from "lodash-es/isNull"
import isNumber from "lodash-es/isNumber"
import isUndefined from "lodash-es/isUndefined"
import lowerCase from "lodash-es/lowerCase"
import map from "lodash-es/map"
import reduce from "lodash-es/reduce"

import type { TranslationObj } from "$/utils/lang"

import type {
    IChapterAdvancedContentComponentDto,
    IChapterBaseDto,
    IChapterCoachingMomentComponentDto,
    IChapterCoachingMomentSeedQuestionDto,
    IChapterComponentDto,
    IChapterContentComponentDto,
    IChapterDto,
    IChapterLikertComponentDto,
    IChapterLikertComponentQuestionDto,
    IChapterLikertComponentSummaryDto,
    IChapterVideoComponentDto,
    ISelfDiagnosticQuestionOptionDto,
    TChapterComponentType,
    TChapterStatus
} from "@/3514/types/dtos"
import {
    EChapterAdvancedContentComponentAppearanceCount,
    EChapterComponentType,
    EChapterStatus,
    EChapterType,
    type IChapterAdvancedContentComponentModel,
    IChapterBaseModel,
    type IChapterCoachingMomentComponentModel,
    type IChapterCoachingMomentSeedQuestionModel,
    type IChapterComponentModel,
    type IChapterContentComponentModel,
    type IChapterLikertComponentModel,
    type IChapterLikertComponentQuestionModel,
    type IChapterLikertComponentSummaryModel,
    type IChapterModel,
    type IChapterVideoComponentModel,
    type ISelfDiagnosticQuestionOptionModel
} from "@/3514/types/models"

import { selfAssessmentSummaryAdapter } from "./self-assessment.adapter"

function _getChapterComponentEnumFromType(type: TChapterComponentType): EChapterComponentType {
    switch (type) {
        case "question_group":
            return EChapterComponentType.QuestionGroup
        case "video_vimeo":
            return EChapterComponentType.VideoVimeo
        case "content":
            return EChapterComponentType.Content
        case "advanced_content":
            return EChapterComponentType.AdvancedContent
        case "coaching_moment_chat":
            return EChapterComponentType.CoachingMoment
        case null:
        default:
            return null
    }
}

function _getContentComponentModel(
    model: IChapterComponentModel,
    { content, title }: IChapterContentComponentDto
): IChapterContentComponentModel {
    return { ...model, content, title }
}

function _getAdvancedContentComponentAppearanceCount(index: number): EChapterAdvancedContentComponentAppearanceCount {
    switch (index) {
        case 0:
        case 3:
            return EChapterAdvancedContentComponentAppearanceCount.First
        case 1:
        case 4:
            return EChapterAdvancedContentComponentAppearanceCount.Second
        case 2:
        case 5:
            return EChapterAdvancedContentComponentAppearanceCount.Third
        default:
            return EChapterAdvancedContentComponentAppearanceCount.First
    }
}

function _getAdvancedContentIndexMap(chapterComponents: IChapterComponentDto[]): Record<string, number> {
    const typeCounters: Map<string, number> = new Map<string, number>()

    const uuidToIndexRecord: Record<string, number> = {}

    forEach<IChapterComponentDto>(chapterComponents, (item: IChapterComponentDto): void => {
        if (eq(item.type, "advanced_content")) {
            const currentIndex: number = typeCounters.get(item.type) ?? 0
            uuidToIndexRecord[item.uuid] = currentIndex
            typeCounters.set(item.type, currentIndex + 1)
        }
    })

    return uuidToIndexRecord
}

function _getAdvancedContentComponentModel(
    model: IChapterComponentModel,
    {
        title,
        intro_content,
        middle_content_title: featureTitle,
        middle_content: featureContent,
        bottom_content
    }: IChapterAdvancedContentComponentDto,
    appearanceIndex: number
): IChapterAdvancedContentComponentModel {
    return {
        ...model,
        title: isEmpty(title) ? null : title,
        introContent: isEmpty(intro_content) ? null : intro_content,
        featureTitle,
        featureContent,
        outroContent: isEmpty(bottom_content) ? null : bottom_content,
        appearanceCount: _getAdvancedContentComponentAppearanceCount(appearanceIndex)
    }
}

function _getVideoComponentModel(
    model: IChapterComponentModel,
    { video_id: videoId, title }: IChapterVideoComponentDto
): IChapterVideoComponentModel {
    return { ...model, videoId, title }
}

function _getCoachingMomentComponentModel<TDto extends IChapterCoachingMomentComponentDto>(
    model: IChapterComponentModel,
    {
        is_chat_available: isChatAvailable,
        is_chat_completed: isChatCompleted,
        prev_chat_completed_at: previousChatCompletionDate,
        prev_chat_chapter_progress_id: previousChatChapterId,
        prev_summary_cards: previousSummaryCards,
        summary_template_cards: actualTemplateCards,
        seed_questions,
        answer_data,
        title
    }: TDto
): IChapterCoachingMomentComponentModel {
    return {
        ...model,
        isChatAvailable,
        isChatCompleted,
        previousChatCompletionDate,
        previousChatChapterId,
        title,
        previousSummaryCards: isUndefined(previousSummaryCards) ? [] : previousSummaryCards,
        actualSummaryCards: isEmpty(actualTemplateCards) ? undefined : actualTemplateCards,
        seedQuestions: isEmpty(seed_questions)
            ? []
            : map<IChapterCoachingMomentSeedQuestionDto, IChapterCoachingMomentSeedQuestionModel>(
                  seed_questions,
                  ({
                      question_uuid: questionUuid,
                      question
                  }: IChapterCoachingMomentSeedQuestionDto): IChapterCoachingMomentSeedQuestionModel => ({
                      questionUuid,
                      question
                  })
              ),
        answerData: isEmpty(answer_data)
            ? undefined
            : {
                  conversationId: answer_data.conversation,
                  seedQuestions: isEmpty(answer_data?.seed_questions_data)
                      ? []
                      : map(
                            answer_data.seed_questions_data,
                            ({
                                question_uuid: questionUuid,
                                question,
                                answer
                            }: IChapterCoachingMomentSeedQuestionDto & {
                                answer: string
                            }): IChapterCoachingMomentSeedQuestionModel & { answer: string } => ({
                                questionUuid,
                                question,
                                answer
                            })
                        )
              }
    }
}

function _getLikertComponentModel(
    model: IChapterComponentModel,
    {
        title,
        question_list,
        summary_list,
        show_summary_after_finish: isSummaryAfterCompletionShown,
        editable_after_finish: isEditableAfterCompletion,
        answer_data
    }: IChapterLikertComponentDto
): IChapterLikertComponentModel {
    return {
        ...model,
        title,
        isSummaryAfterCompletionShown,
        isEditableAfterCompletion,
        answerData: isUndefined(answer_data)
            ? undefined
            : {
                  rank: answer_data.rank,
                  answerList: isEmpty(answer_data.answer_list)
                      ? []
                      : map(
                            answer_data.answer_list,
                            ({
                                allow_free_response: isFreeResponseAllowed,
                                free_response: freeResponsePrompt,
                                not_applicable: isNotApplicable,
                                not_applicable_label: notApplicableLabel,
                                option_uuid: optionUuid,
                                options_number: optionsCount,
                                question_text: questionText,
                                question_uuid: questionUuid,
                                rank
                            }) => ({
                                isFreeResponseAllowed,
                                freeResponsePrompt,
                                isNotApplicable,
                                notApplicableLabel,
                                optionUuid,
                                optionsCount,
                                questionText,
                                questionUuid,
                                rank
                            })
                        )
              },
        summaryList: reduce<IChapterLikertComponentSummaryDto, IChapterLikertComponentSummaryModel[]>(
            summary_list,
            (
                acc: IChapterLikertComponentSummaryModel[],
                { uuid, ...rest }: IChapterLikertComponentSummaryDto
            ): IChapterLikertComponentSummaryModel[] =>
                concat(acc, {
                    ...selfAssessmentSummaryAdapter({ ...rest }),
                    uuid
                }),
            []
        ),
        questionList: isEmpty(question_list)
            ? []
            : reduce<IChapterLikertComponentQuestionDto, IChapterLikertComponentQuestionModel[]>(
                  question_list,
                  (
                      acc: IChapterLikertComponentQuestionModel[],
                      {
                          not_applicable: isNotApplicable,
                          not_applicable_label: notApplicableLabel,
                          not_applicable_prompt: notApplicablePrompt,
                          option_numbers: optionsCount,
                          options,
                          order,
                          question,
                          uuid
                      }: IChapterLikertComponentQuestionDto
                  ): IChapterLikertComponentQuestionModel[] =>
                      concat(acc, {
                          uuid,
                          question,
                          order,
                          optionsCount,
                          isNotApplicable,
                          notApplicableLabel,
                          notApplicablePrompt,
                          options: reduce<ISelfDiagnosticQuestionOptionDto, ISelfDiagnosticQuestionOptionModel[]>(
                              options,
                              (
                                  optAcc: ISelfDiagnosticQuestionOptionModel[],
                                  {
                                      uuid: optUuid,
                                      response: optResponse,
                                      allow_free_response: optAllowFreeResponse,
                                      free_response_prompt: optFreeResponsePrompt
                                  }: ISelfDiagnosticQuestionOptionDto
                              ): ISelfDiagnosticQuestionOptionModel[] =>
                                  concat(optAcc, {
                                      uuid: optUuid,
                                      isFreeResponseAllowed: Boolean(optAllowFreeResponse),
                                      freeResponsePrompt: optFreeResponsePrompt,
                                      response: optResponse
                                  }),
                              []
                          )
                      }),
                  []
              )
    }
}

function _getBaseChapterComponentModel({
    uuid,
    id,
    order,
    type: dType,
    published: isPublished,
    required: isRequired
}: IChapterComponentDto): IChapterComponentModel {
    return {
        uuid,
        id,
        order,
        isPublished,
        isRequired,
        type: _getChapterComponentEnumFromType(dType)
    } as IChapterComponentModel
}

function _getChapterComponentModels(components: IChapterComponentDto[]): IChapterComponentModel[] {
    const advancedContentIndexRecord: Record<string, number> = _getAdvancedContentIndexMap(components)

    return map<IChapterComponentDto, IChapterComponentModel>(
        components,
        (dto: IChapterComponentDto): IChapterComponentModel => {
            if (isEmpty(dto)) return null

            const baseModel: IChapterComponentModel = _getBaseChapterComponentModel(dto)

            switch (baseModel.type) {
                case EChapterComponentType.Content:
                    return _getContentComponentModel(baseModel, dto as IChapterContentComponentDto)
                case EChapterComponentType.AdvancedContent:
                    return _getAdvancedContentComponentModel(
                        baseModel,
                        dto as IChapterAdvancedContentComponentDto,
                        advancedContentIndexRecord[dto.uuid]
                    )
                case EChapterComponentType.QuestionGroup:
                    return _getLikertComponentModel(baseModel, dto as IChapterLikertComponentDto)
                case EChapterComponentType.VideoVimeo:
                    return _getVideoComponentModel(baseModel, dto as IChapterVideoComponentDto)
                case EChapterComponentType.CoachingMoment:
                    return _getCoachingMomentComponentModel(baseModel, dto as IChapterCoachingMomentComponentDto)
                default:
                    return null
            }
        }
    )
}

function _getChapterStatusEnumFromType(status: TChapterStatus): EChapterStatus {
    switch (status) {
        case "open":
            return EChapterStatus.Open
        case "in_progress":
            return EChapterStatus.InProgress
        case "complete":
            return EChapterStatus.Completed
        case null:
        default:
            return null
    }
}

function _getChapterTypeFromTitle(title: TranslationObj): EChapterType {
    switch (lowerCase(title.en)) {
        case "access":
            return EChapterType.Access
        case "learn":
            return EChapterType.Learn
        case "prepare":
            return EChapterType.Prepare
        case "reflect":
            return EChapterType.Reflect
        case "practice":
            return EChapterType.Practice
        default:
            return null
    }
}

function chapterBaseAdapter(dto: IChapterBaseDto): IChapterBaseModel {
    return {
        components: isEmpty(dto.components)
            ? []
            : _getChapterComponentModels(filter<IChapterComponentDto>(dto.components, "published")),
        enrollmentId: dto.enrollment_id,
        id: dto.id,
        moduleTitle: dto.module_title,
        nextChapterId: dto.next_chapter_progress_id,
        phaseOrder: dto.phase_order,
        phaseProgressId: dto.phase_progress_id,
        previousChapterId: dto.previous_chapter_progress_id,
        status: _getChapterStatusEnumFromType(dto.status),
        title: dto.title,
        uuid: dto.uuid
    }
}

function chapterAdapter(dto: IChapterDto): IChapterModel {
    return isEmpty(dto)
        ? null
        : isNull(dto?.redirect_chapter_progress_id) || isNumber(dto?.redirect_chapter_progress_id)
          ? ({
                redirectChapterId: dto.redirect_chapter_progress_id
            } as IChapterModel)
          : {
                ...chapterBaseAdapter(dto),
                type: _getChapterTypeFromTitle(dto.title)
            }
}

export { chapterBaseAdapter, chapterAdapter }
